import classNames from 'classnames';
import Head from 'next/head';
import {useRouter} from 'next/router';
import React, {useEffect} from 'react';

import {HeadSEO} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {MainContentLoader} from '@udemy/react-reveal-components';
import {useUDData} from '@udemy/ud-data';

import {getRuntimeConfig} from 'src/pages/api/config.api';

import {AssertionDetailBanner} from '../../../../components/assertion-detail-banner.react-component';
import styles from '../../../../components/assertions-page.module.less';
import {CoursesList} from '../../../../components/courses-list.react-component';
import {EmptyAssertionState} from '../../../../components/empty-preparation-summary.react-component';
import {PreparationSummary} from '../../../../components/preparation-summary.react-component';
import {useAssertion} from '../../../../hooks/useAssertion';
import {useEnrolledCourses} from '../../../../hooks/useEnrolledCourses';

interface AssertionPageProps {
    assertionId: string;
}

export const AssertionPage = ({assertionId}: AssertionPageProps) => {
    const {gettext} = useI18n();
    const {Config, me} = useUDData();
    const {assertion, assertionStore, loading} = useAssertion(assertionId);

    const router = useRouter();

    const userId = assertion?.user.id ?? '';
    const topicId = assertion?.badgeClass.topic?.id ?? '';

    const {enrolledCourses, isLoaded} = useEnrolledCourses(userId, topicId);
    const isAssertionOwnerView = me.is_authenticated && me.id === parseInt(userId, 10);

    useEffect(() => {
        if (!loading && assertion === undefined) {
            router.push('/404');
        }
    }, [assertion, loading, router]);

    const certPrepForAssertion = (
        <>
            {topicId !== '' && (
                <span className="ud-heading-xl">{gettext('Certification preparation')}</span>
            )}
            {!isLoaded ? (
                <MainContentLoader />
            ) : (
                topicId !== '' &&
                (enrolledCourses.length > 0 ? (
                    <>
                        <PreparationSummary courses={enrolledCourses} />
                        <div className="ud-heading-xl">
                            {gettext('Courses taken to prepare for this certification exam')}
                        </div>
                        <CoursesList courses={enrolledCourses} />
                    </>
                ) : (
                    <EmptyAssertionState isOwner={isAssertionOwnerView} />
                ))
            )}
        </>
    );

    return (
        <>
            <HeadSEO
                ogTitle={gettext('Badges certification')}
                // description={gettext('This is an example page for a new Frontends application')}
                ogDescription={gettext('This is an example page for a new Frontends application')}
                ogType="some_opengraph_type"
                // ogLocale={process.env.I18N_PATH_TO_BUNDLE_MAP.get(locale)}
                canonicalUrl={`${getRuntimeConfig('MONOLITH_URL')}/example/`}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <div
                data-testid="assertion-page-container"
                className={classNames(styles['assertion-page-container'], 'ud-container')}
            >
                <AssertionDetailBanner assertionStore={assertionStore} />
                {assertion && Config.brand.has_organization && certPrepForAssertion}
            </div>
        </>
    );
};
